import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js";

export default class extends Controller {
  static targets = ["text", "form", "input"]
  static values = { 
    url: String,
    field: String,
    model: String,
    textarea: { type: Boolean, default: false }
  }

  connect() {
    // Set default values if not provided
    if (!this.hasUrlValue) {
      this.urlValue = window.location.pathname
    }
    if (!this.hasFieldValue) {
      this.fieldValue = 'text'
    }
    if (!this.hasModelValue) {
      this.modelValue = 'item'
    }

    // Store bound handler once to ensure add/remove use the same reference
    this.boundHandleKeydown = this.handleKeydown.bind(this)
  }

  editText(event) {
    event.preventDefault()
    
    if (this.hasTextTarget && this.hasFormTarget) {
      this.textTarget.style.display = 'none'
      this.formTarget.style.display = 'block'
      this.inputTarget.focus()
      
      // Move cursor to end of text
      const length = this.inputTarget.value.length
      this.inputTarget.setSelectionRange(length, length)
      
      // Auto-resize textarea on edit
      if (this.textareaValue) {
        this.autoResize()
      }
      
      // Add keyboard event listeners
      this.inputTarget.addEventListener('keydown', this.boundHandleKeydown)
    }
  }

  handleKeydown(event) {
    if (this.textareaValue) {
      // For textarea: Cmd/Ctrl+Enter to save, Escape to cancel
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        this.saveText(event)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        this.cancelEdit(event)
      }
    } else {
      // For input: Enter to save, Escape to cancel
      if (event.key === 'Enter') {
        event.preventDefault()
        this.saveText(event)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        this.cancelEdit(event)
      }
    }
  }

  autoResize() {
    this.inputTarget.style.height = 'auto'
    this.inputTarget.style.height = this.inputTarget.scrollHeight + 'px'
  }

  saveText(event) {
    event.preventDefault()
    
    const text = this.inputTarget.value
    
    // Get CSRF token from meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    
    // Build the request body dynamically
    const requestBody = {}
    requestBody[this.modelValue] = {}
    requestBody[this.modelValue][this.fieldValue] = text
  
    // Make AJAX call to save text to the server
    fetch(this.urlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => {
      
      if (response.ok) {
        // Update the display and hide the form
        if (text.trim()) {
          // Find the text span and update only its content
          const textSpan = this.textTarget.querySelector('.inline-edit__text')
          if (textSpan) {
            textSpan.textContent = text
          } else {
            // If no text span exists, create one
            this.textTarget.innerHTML = `<span class='inline-edit__text'>${text}</span><i class='far fa-edit'></i>`
          }
        } else {
          // Show placeholder text
          this.textTarget.innerHTML = `<span class='inline-edit__placeholder'>${this.element.dataset.placeholder || 'Click to edit'}</span><i class='far fa-edit'></i>`
        }
        this.formTarget.style.display = 'none'
        this.textTarget.style.display = 'flex'
        
        // Remove keyboard event listener
        this.inputTarget.removeEventListener('keydown', this.boundHandleKeydown)

        // Dispatch a custom event for other controllers to listen to
        this.dispatch('saved', { detail: { text, field: this.fieldValue } })
        flash("Updated", "success");
      } else {
        flash("Failed to update", "error");
        // You could show an error message here
      }
    })
    .catch(error => {
      flash("Error updating", "error");
      console.error('Error saving text:', error)
      // You could show an error message here
    })
  }

  cancelEdit(event) {
    event.preventDefault()
    
    if (this.hasTextTarget && this.hasFormTarget) {
      this.formTarget.style.display = 'none'
      this.textTarget.style.display = 'flex'
      
      // Reset input to the current displayed text (not the input's current value)
      const textSpan = this.textTarget.querySelector('.inline-edit__text')
      const currentText = textSpan ? textSpan.textContent : ''
      this.inputTarget.value = currentText
      
      // Remove keyboard event listener
      this.inputTarget.removeEventListener('keydown', this.boundHandleKeydown)
    }
  }

}

